import React, { useEffect, useRef, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input, Textarea } from "../components/UI";
import { toast } from "sonner";
import { Palette, Loader2, Plus, Trash2, Upload, ShieldCheck, ImageIcon, FileVideo, FileText,
         Image as ImageLucide, Save, ExternalLink } from "lucide-react";

const KINDS = [
  { key: "logo", label: "Logo", icon: ImageLucide },
  { key: "photo", label: "Photo", icon: ImageIcon },
  { key: "video", label: "Video", icon: FileVideo },
  { key: "doc", label: "Doc / PDF", icon: FileText },
];

const usdIcon = ({ children }) => children;
void usdIcon;

// Convert File → data URL and downscale images to max 1280px width for size safety
const fileToDataUrl = (file, maxWidth = 1280) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error("Could not read file"));
  reader.onload = () => {
    const dataUrl = reader.result;
    if (!file.type.startsWith("image/") || file.type === "image/svg+xml") return resolve(dataUrl);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      if (scale >= 1) return resolve(dataUrl);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL(file.type === "image/png" ? "image/png" : "image/jpeg", 0.85));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  };
  reader.readAsDataURL(file);
});

const ListEditor = ({ label, hint, items, onChange, testidPrefix, placeholder }) => {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(items || []), v]);
    setDraft("");
  };
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">{label}</label>
      {hint && <div className="text-[11px] text-slate-500 mb-2">{hint}</div>}
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testidPrefix}-list`}>
        {(items || []).map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-xs bg-white/[0.05] border border-white/10 text-slate-200"
                data-testid={`${testidPrefix}-item-${i}`}>
            {it}
            <button onClick={() => onChange(items.filter((_, k) => k !== i))}
                    className="w-4 h-4 rounded-full text-slate-500 hover:text-rose-400 inline-flex items-center justify-center"
                    aria-label="Remove">
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
               placeholder={placeholder}
               data-testid={`${testidPrefix}-input`}
               className="flex-1 bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none placeholder-slate-500" />
        <button onClick={add} data-testid={`${testidPrefix}-add`}
                className="text-xs px-3 py-2 rounded-md border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
    </div>
  );
};

export default function BrandLibrary() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState("photo");
  const fileRef = useRef(null);

  const load = () => api.get("/political-os/brand-library")
    .then((r) => setData(r.data))
    .catch((e) => toast.error(errMsg(e, "Could not load brand library")));

  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;
  const bk = data.brand_kit || {};

  const update = (patch) => setData({ ...data, brand_kit: { ...bk, ...patch } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/political-os/brand-library", bk);
      toast.success("Brand library saved — every AI creative will pull from this now.");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const upload = async (files) => {
    if (!files || !files.length) return;
    const file = files[0];
    if (file.size > 5_000_000) return toast.error("File too large (max 5 MB)");
    setUploading(true);
    try {
      const url = await fileToDataUrl(file);
      await api.post("/political-os/brand-library/assets", {
        kind, name: file.name.replace(/\.[^.]+$/, "").slice(0, 100), url,
      });
      toast.success(`${kind} uploaded`);
      load();
    } catch (e) { toast.error(errMsg(e, "Upload failed")); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeAsset = async (id) => {
    try {
      await api.delete(`/political-os/brand-library/assets/${id}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="brand-library-page">
      <PageHeader eyebrow="Brand Brain" title="Campaign Asset Library"
        subtitle="One central place for bios, logos, photos, talking points and approved claims — auto-injected into every AI creative draft, email and ad."
        action={<Btn onClick={save} disabled={saving} data-testid="brand-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save library
        </Btn>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Identity */}
        <Card data-testid="brand-identity-card">
          <h3 className="font-display text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Palette className="w-4 h-4 text-cyan-300" /> Identity
          </h3>
          <div className="space-y-3">
            <Input label="Campaign tagline" value={bk.tagline || ""}
                   onChange={(e) => update({ tagline: e.target.value })}
                   data-testid="brand-tagline"
                   placeholder="A stronger Texas for every family" />
            <Input label="Fundraising URL" value={bk.fundraising_url || ""}
                   onChange={(e) => update({ fundraising_url: e.target.value })}
                   data-testid="brand-fundraising-url"
                   placeholder="https://your-campaign.com/donate" />
            <Input label="Voice / tone" value={bk.tone || ""}
                   onChange={(e) => update({ tone: e.target.value })}
                   data-testid="brand-tone"
                   placeholder="Warm, direct, no jargon" />
            <Textarea label="Candidate bio (used in AI drafts)" value={bk.bio || ""}
                      onChange={(e) => update({ bio: e.target.value })}
                      data-testid="brand-bio"
                      rows={4}
                      placeholder="2-4 sentence bio, background, and what you're running on…" />
            <Textarea label="Compliance disclaimer" value={bk.disclaimer || ""}
                      onChange={(e) => update({ disclaimer: e.target.value })}
                      data-testid="brand-disclaimer"
                      rows={2}
                      placeholder="Paid for by Committee to Elect Jane Smith" />
          </div>
        </Card>

        {/* Messaging */}
        <Card data-testid="brand-messaging-card">
          <h3 className="font-display text-lg font-bold text-white mb-4 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-cyan-300" /> Messaging
          </h3>
          <div className="space-y-4">
            <ListEditor label="Slogans" items={bk.slogans}
                        onChange={(v) => update({ slogans: v })}
                        testidPrefix="brand-slogans"
                        placeholder="Real leadership. Real results." />
            <ListEditor label="Key issues" items={bk.key_issues}
                        onChange={(v) => update({ key_issues: v })}
                        testidPrefix="brand-issues"
                        placeholder="Housing affordability" />
            <ListEditor label="Talking points" hint="Short punchy facts your ads should reinforce."
                        items={bk.talking_points}
                        onChange={(v) => update({ talking_points: v })}
                        testidPrefix="brand-tp"
                        placeholder="Cut property taxes by 12% in 2 years" />
            <ListEditor label="Approved CTAs" hint="Call-to-action lines the AI is allowed to use."
                        items={bk.ctas}
                        onChange={(v) => update({ ctas: v })}
                        testidPrefix="brand-ctas"
                        placeholder="Chip in $10 today" />
          </div>
        </Card>
      </div>

      {/* Asset upload */}
      <Card data-testid="brand-assets-card">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
            <Upload className="w-4 h-4 text-cyan-300" /> Uploaded assets
          </h3>
          <div className="flex flex-wrap gap-2 items-center" data-testid="brand-upload-controls">
            <div className="flex rounded-full border border-white/15 p-0.5">
              {KINDS.map(({ key, label, icon: Icon }) => (
                <button key={key} onClick={() => setKind(key)}
                        data-testid={`brand-kind-${key}`}
                        className={`text-xs px-3 py-1 rounded-full inline-flex items-center gap-1 transition ${kind === key ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/40" : "text-slate-400 hover:text-white"}`}>
                  <Icon className="w-3 h-3" /> {label}
                </button>
              ))}
            </div>
            <input ref={fileRef} type="file"
                   accept={kind === "video" ? "video/*" : kind === "doc" ? ".pdf,application/pdf" : "image/*"}
                   onChange={(e) => upload(e.target.files)}
                   data-testid="brand-file-input"
                   className="text-xs text-slate-300 file:mr-2 file:text-xs file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-gradient-to-r file:from-cyan-400 file:to-blue-500 file:text-slate-900 file:font-semibold" />
            {uploading && <Loader2 className="w-4 h-4 animate-spin text-cyan-300" />}
          </div>
        </div>
        {data.assets.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-500">
            No assets uploaded yet. Upload logos, hero photos, or a signed compliance doc — they&apos;ll appear in Ad Studio automatically.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="brand-assets-grid">
            {data.assets.map((a) => (
              <div key={a.id} className="group relative aspect-square rounded-lg border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={`brand-asset-${a.id}`}>
                {(a.kind === "logo" || a.kind === "photo") && a.url ? (
                  <img src={a.url} alt={a.name} className="w-full h-full object-contain p-2" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 gap-2">
                    {a.kind === "video" && <FileVideo className="w-8 h-8" />}
                    {a.kind === "doc" && <FileText className="w-8 h-8" />}
                    <div className="text-[10px] uppercase tracking-wider">{a.kind}</div>
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent px-2 py-1.5">
                  <div className="text-[10px] text-white truncate">{a.name}</div>
                </div>
                <button onClick={() => removeAsset(a.id)}
                        data-testid={`brand-asset-remove-${a.id}`}
                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-slate-900/80 text-slate-400 hover:text-rose-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Approved claims (read-only reference from Claims Vault) */}
      <Card data-testid="brand-claims-card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-300" /> Approved claims
          </h3>
          <a href="/claims-vault" className="text-xs text-cyan-300 hover:underline inline-flex items-center gap-1" data-testid="brand-open-claims-vault">
            Open Claims Vault <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <div className="text-[11px] text-slate-500 mb-3">Only claims marked <b>approved</b> in the Claims Vault flow into AI-generated ad copy.</div>
        {data.approved_claims.length === 0 ? (
          <div className="text-sm text-slate-500 py-3">No approved claims yet — add sourced claims in the Claims Vault.</div>
        ) : (
          <ul className="space-y-2" data-testid="brand-approved-claims-list">
            {data.approved_claims.slice(0, 20).map((c) => (
              <li key={c.id} className="text-xs bg-white/[0.02] border border-white/5 rounded-md px-3 py-2 flex items-start gap-2" data-testid={`brand-claim-${c.id}`}>
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-300 shrink-0 mt-0.5" />
                <div>
                  <div className="text-slate-200">{c.claim}</div>
                  {c.source_url && (
                    <a href={c.source_url} target="_blank" rel="noreferrer"
                       className="text-[10px] text-cyan-300 hover:underline">{c.source || c.source_url}</a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
